import {
    Box,
    Button,
    Checkbox,
    IconButton,
    Paper,
    Radio,
    TextField,
    Typography
} from '@mui/material';
import { styled } from '@mui/system';

import { colors } from '../../../../theme';

const goldenYellowGradient = `linear-gradient(105.23deg, ${colors.lightningYellow} 0%, ${colors.pirateGold} 100%)`;

const planningButtonBase = {
    textTransform: 'none' as const,
    fontFamily: '"Inter", sans-serif',
    fontSize: '14px',
    fontWeight: 600,
    borderRadius: '100px',
    border: 'none',
    boxShadow: 'none',
    color: colors.white,
    '&:hover': { boxShadow: 'none' },
    '&:active': { boxShadow: 'none' }
};

export const SectionRoot = styled(Box)`
    width: 100%;
    padding: 0 4px;
`;

export const SectionTitle = styled(Typography)`
    font:
        600 16px/19px 'Inter',
        sans-serif;
    color: ${colors.white90};
    margin-bottom: 12px;
`;

export const ErrorBox = styled(Box)`
    padding: 24px;
    text-align: center;
    width: 100%;
`;

export const errorText = {
    fontFamily: '"Inter", sans-serif',
    fontSize: '14px',
    color: '#ff6b6b',
    marginBottom: '16px'
};

export const successText = {
    fontFamily: '"Inter", sans-serif',
    fontSize: '14px',
    color: colors.white90,
    marginBottom: '16px'
};

export const QuestionCard = styled(Paper)`
    width: 100%;
    margin-bottom: 16px;
    background-color: ${colors.darkGray};
    border-radius: 12px;
    overflow: hidden;
    box-sizing: border-box;
`;

export const QuestionHeader = styled(Box)`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 24px;
    cursor: pointer;
    user-select: none;
    &:hover {
        background-color: rgba(255, 255, 255, 0.03);
    }
`;

export const QuestionBody = styled(Box)`
    padding: 0 24px 24px;
`;

export const QuestionText = styled(Typography)`
    font:
        500 14px/18px 'Inter',
        sans-serif;
    color: ${colors.white90};
`;

export const QuestionTitleRow = styled(Box)`
    display: flex;
    align-items: center;
    min-width: 0;
`;

export const SkippedLabel = styled(Typography)`
    color: ${colors.white50};
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    font-style: italic;
    margin-left: 8px;
    flex-shrink: 0;
`;

export const QuestionExpandIcon = styled(IconButton)`
    color: ${colors.white50};
    flex-shrink: 0;
    margin-left: 8px;
`;

export const OptionCheckbox = styled(Checkbox)`
    color: ${colors.white50};
    &.Mui-checked {
        color: ${colors.lightningYellow};
    }
`;

export const OptionRadio = styled(Radio)`
    color: ${colors.white50};
    &.Mui-checked {
        color: ${colors.lightningYellow};
    }
`;

export const OptionLabel = styled(Typography)`
    color: ${colors.white90};
    font-family: 'Inter', sans-serif;
    font-size: 14px;
`;

export const FreeTextField = styled(TextField)`
    margin-top: 8px;
    & .MuiOutlinedInput-root {
        color: ${colors.white90};
        font-family: 'Inter', sans-serif;
        font-size: 14px;
        & fieldset {
            border-color: ${colors.yellow60};
        }
        &:hover fieldset {
            border-color: ${colors.lightningYellow};
        }
        &.Mui-focused fieldset {
            border-color: ${colors.lightningYellow};
        }
    }
`;

export const SkipButton = styled(Button)`
    color: ${colors.white50};
    text-transform: none;
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    font-weight: 500;
    padding: 4px 12px;
    border-color: rgba(255, 255, 255, 0.15);
    &:hover {
        border-color: rgba(255, 255, 255, 0.3);
    }
`;

export const SkipButtonRow = styled(Box)`
    display: flex;
    justify-content: flex-end;
    margin-top: 16px;
`;

export const QuestionsHeaderRow = styled(Box)`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

export const SectionCollapseIcon = styled(IconButton)`
    color: ${colors.white50};
`;

export const ActionButtonsRow = styled(Box)`
    display: flex;
    gap: 12px;
    margin-top: 24px;
    justify-content: flex-end;
`;

export const PlanReadyRoot = styled(Box)`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    padding: 120px 24px 64px;
    box-sizing: border-box;
`;

export const PlanReadyContent = styled(Box)`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: fit-content;
    max-width: min(560px, 100%);
`;

export const PlanReadyMessage = styled(Typography)`
    font:
        600 16px/19px 'Inter',
        sans-serif;
    color: ${colors.white90};
    text-align: center;
`;

export const PlanReadyActionsRow = styled(Box)`
    display: flex;
    margin-top: 16px;
    justify-content: center;
`;

export const SubmitAnswersButton = styled(Button)({
    ...planningButtonBase,
    padding: '10px 32px',
    background: goldenYellowGradient,
    '&:hover': {
        background: goldenYellowGradient,
        filter: 'brightness(1.1)'
    },
    '&.Mui-disabled': {
        background: 'rgba(255, 255, 255, 0.12)',
        color: 'rgba(255, 255, 255, 0.3)'
    }
});

export const PrimaryActionButton = styled(Button)({
    ...planningButtonBase,
    padding: '8px 24px',
    background: goldenYellowGradient,
    '&:hover': {
        background: goldenYellowGradient,
        filter: 'brightness(1.1)'
    },
    '&.Mui-disabled': {
        background: 'rgba(255, 255, 255, 0.12)',
        color: 'rgba(255, 255, 255, 0.3)'
    }
});

export const submitProgressSx = {
    color: colors.darkBlue
};
